import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
export class IOSNotificationWrapper {
    platform = 'ios';
    config;
    isInitialized = false;
    nextNotificationId = 1;
    constructor(config = {}) {
        this.config = {
            requestPermissionOnInit: true,
            enableSound: true,
            enableVibration: false, // iOS handles vibration automatically
            importance: 'default',
            ...config
        };
    }
    async initialize() {
        if (this.isInitialized)
            return true;
        try {
            // Check if Capacitor and LocalNotifications are available
            if (!this.isSupported()) {
                console.warn('[IOSNotificationWrapper] LocalNotifications not available');
                return false;
            }
            // Request permission if configured to do so
            if (this.config.requestPermissionOnInit) {
                const result = await this.requestPermission();
                if (!result.granted) {
                    console.warn('[IOSNotificationWrapper] Permission not granted during init');
                }
            }
            // Set up event listeners
            this.setupEventListeners();
            this.isInitialized = true;
            console.log('[IOSNotificationWrapper] Initialized successfully');
            return true;
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Initialization failed:', error);
            return false;
        }
    }
    async destroy() {
        if (!this.isInitialized)
            return;
        try {
            // Cancel all notifications
            await this.cancelAll();
            // Remove listeners
            await LocalNotifications.removeAllListeners();
            this.isInitialized = false;
            console.log('[IOSNotificationWrapper] Destroyed');
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Error during destroy:', error);
        }
    }
    isSupported() {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
    }
    getPermissionStatus() {
        // Note: Capacitor doesn't provide a synchronous way to get permission status
        // This would need to be cached from the last permission check
        return 'default'; // Fallback - should be updated after permission checks
    }
    async requestPermission() {
        if (!this.isSupported()) {
            return { granted: false, permission: 'denied' };
        }
        try {
            const result = await LocalNotifications.requestPermissions();
            const granted = result.display === 'granted';
            const permission = granted ? 'granted' : 'denied';
            console.log(`[IOSNotificationWrapper] Permission ${granted ? 'granted' : 'denied'}`);
            return { granted, permission: permission };
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Permission request failed:', error);
            return { granted: false, permission: 'denied' };
        }
    }
    async sendImmediate(payload) {
        if (!this.isSupported()) {
            throw new Error('LocalNotifications not supported on this platform');
        }
        const notificationId = this.getNextId();
        const notification = this.payloadToCapacitorNotification(payload, notificationId);
        try {
            await LocalNotifications.schedule({
                notifications: [notification]
            });
            console.log('[IOSNotificationWrapper] Immediate notification sent:', payload.title);
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Failed to send immediate notification:', error);
            throw error;
        }
    }
    async schedule(payload) {
        if (!this.isSupported()) {
            throw new Error('LocalNotifications not supported on this platform');
        }
        const notificationId = this.getNextId();
        const notification = this.payloadToCapacitorNotification(payload, notificationId, payload.scheduleTime);
        try {
            await LocalNotifications.schedule({
                notifications: [notification]
            });
            console.log(`[IOSNotificationWrapper] Notification scheduled for ${payload.scheduleTime.toISOString()}: ${payload.title}`);
            return notificationId.toString();
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Failed to schedule notification:', error);
            throw error;
        }
    }
    async cancel(id) {
        if (!this.isSupported())
            return;
        try {
            await LocalNotifications.cancel({
                notifications: [{ id: parseInt(id, 10) }]
            });
            console.log(`[IOSNotificationWrapper] Cancelled notification: ${id}`);
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Failed to cancel notification:', error);
        }
    }
    async cancelAll() {
        if (!this.isSupported())
            return;
        try {
            const pending = await LocalNotifications.getPending();
            if (pending.notifications.length > 0) {
                await LocalNotifications.cancel({
                    notifications: pending.notifications.map(n => ({ id: n.id }))
                });
            }
            console.log('[IOSNotificationWrapper] All notifications cancelled');
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Failed to cancel all notifications:', error);
        }
    }
    async cancelByTag(tag) {
        if (!this.isSupported())
            return;
        try {
            const pending = await LocalNotifications.getPending();
            const toCancel = pending.notifications
                .filter(notification => notification.extra?.tag === tag)
                .map(notification => ({ id: notification.id }));
            if (toCancel.length > 0) {
                await LocalNotifications.cancel({ notifications: toCancel });
            }
            console.log(`[IOSNotificationWrapper] Cancelled ${toCancel.length} notifications with tag: ${tag}`);
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Failed to cancel notifications by tag:', error);
        }
    }
    async getPending() {
        if (!this.isSupported())
            return [];
        try {
            const result = await LocalNotifications.getPending();
            return result.notifications.map(this.capacitorNotificationToPayload);
        }
        catch (error) {
            console.error('[IOSNotificationWrapper] Failed to get pending notifications:', error);
            return [];
        }
    }
    getNextId() {
        return this.nextNotificationId++;
    }
    payloadToCapacitorNotification(payload, id, scheduleTime) {
        const notification = {
            id,
            title: payload.title,
            body: payload.body,
            sound: this.config.enableSound ? (payload.sound || 'default') : undefined,
            extra: {
                ...payload.data,
                tag: payload.tag,
                originalPayload: payload
            }
        };
        // Add scheduling if specified
        if (scheduleTime) {
            notification.schedule = {
                at: scheduleTime
            };
        }
        // iOS-specific configurations
        if (payload.badge) {
            notification.iconColor = '#007AFF'; // iOS blue
        }
        // Add attachments for rich notifications (iOS 10+)
        if (payload.image) {
            notification.attachments = [{
                    id: 'image',
                    url: payload.image,
                    options: {}
                }];
        }
        // iOS supports threading (grouping) notifications
        if (payload.tag) {
            notification.threadIdentifier = payload.tag;
        }
        // Set critical alert if requiring interaction (iOS 12+)
        if (payload.requireInteraction) {
            notification.sound = 'critical';
        }
        return notification;
    }
    capacitorNotificationToPayload(notification) {
        const extra = notification.extra || {};
        return {
            id: notification.id?.toString(),
            title: notification.title || '',
            body: notification.body || '',
            icon: notification.smallIcon,
            image: notification.attachments?.[0]?.url,
            tag: extra.tag,
            data: extra,
            sound: notification.sound,
            scheduleTime: notification.schedule?.at ? new Date(notification.schedule.at) : new Date(),
            challengeId: extra.challengeId,
            requireInteraction: notification.sound === 'critical'
        };
    }
    setupEventListeners() {
        // Listen for notification clicks
        LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
            const payload = notification.notification.extra?.originalPayload;
            if (payload) {
                const event = {
                    type: 'notification-click',
                    payload,
                    platform: this.platform
                };
                window.dispatchEvent(new CustomEvent('notification-click', { detail: event }));
            }
            console.log('[IOSNotificationWrapper] Notification clicked:', notification.notification.title);
        });
        // Listen for notification received (when app is in foreground)
        LocalNotifications.addListener('localNotificationReceived', (notification) => {
            const payload = notification.extra?.originalPayload;
            if (payload) {
                const event = {
                    type: 'notification-received',
                    payload,
                    platform: this.platform
                };
                window.dispatchEvent(new CustomEvent('notification-received', { detail: event }));
            }
            console.log('[IOSNotificationWrapper] Notification received:', notification.title);
        });
    }
}
